import { shouldRefreshAxiosSession, shouldRefreshFetchSession } from './sessionRefresh';

// Install once at bootstrap; both transports share the same renewal promise.
export function installSessionClient(client, browser, apiUrl = '') {
  const nativeFetch = browser.fetch.bind(browser);
  const base = new URL(apiUrl || '/', browser.location.href);
  const root = base.pathname.replace(/\/$/, '');
  const resolve = (value, baseURL) => new URL(value, baseURL || browser.location.href);
  const isApi = (url) => url.origin === base.origin &&
    (!root || url.pathname === root || url.pathname.startsWith(`${root}/`));
  const path = (url) => url.pathname.slice(root.length).replace(/\/$/, '');
  const excluded = (url) => /^(\/auth\/(refresh|logout|azure\/exchange)|\/token|\/authusers\/token|\/api\/(consumers|organizations)\/login)$/.test(path(url));
  let inFlight;
  let generation = 0;
  let refreshController;
  // A 403 means the server refuses this origin (stale FRONTEND_ORIGINS), and a
  // 401 means the refresh cookie is dead. Either way, retrying immediately on
  // every request only produces a 401/403 request loop, so renewal backs off.
  let renewalBlockedUntil = 0;
  let renewalFailures = 0;
  let renewalError;
  const FORBIDDEN_BACKOFF_MS = 15000;
  const MAX_RENEWAL_BACKOFF_MS = 60000;

  const refresh = () => {
    if (inFlight) return inFlight;
    if (Date.now() < renewalBlockedUntil) {
      return Promise.reject(renewalError || new Error('Session renewal is temporarily paused'));
    }
    {
      const started = generation;
      refreshController = new AbortController();
      const controller = refreshController;
      const timeout = setTimeout(() => controller.abort(), 4000);
      const renew = async () => {
        if (started !== generation || controller.signal.aborted) throw new Error('Session renewal cancelled');
        let response;
        try {
          response = await client.post(`${apiUrl}/auth/refresh`, null, {
            withCredentials: true, timeout: 4000, signal: controller.signal, __skipSessionRefresh: true,
          });
        } catch (error) {
          renewalError = error;
          const status = error?.response?.status;
          if (status === 403) {
            renewalFailures = 0;
            renewalBlockedUntil = Date.now() + FORBIDDEN_BACKOFF_MS;
            const forbidden = new Error('Session renewal rejected: origin is not allowed');
            forbidden.status = 403;
            throw forbidden;
          }
          if (status === 401) {
            renewalFailures += 1;
            renewalBlockedUntil = Date.now() + Math.min(
              MAX_RENEWAL_BACKOFF_MS, 1000 * 2 ** (renewalFailures - 1),
            );
          }
          throw error;
        }
        if (started !== generation) throw new Error('Session changed during renewal');
        const data = response.data;
        if (!data?.access_token) throw new Error('Renewal returned no access token');
        renewalFailures = 0;
        renewalBlockedUntil = 0;
        renewalError = undefined;
        // Update non-React service callers before notifying React listeners.
        browser.__authContext = { ...browser.__authContext, apiUrl, token: data.access_token };
        browser.dispatchEvent(new CustomEvent('stunm:session-refreshed', { detail: data }));
        return data;
      };
      // Web Locks serialize refresh-cookie rotation across tabs when supported.
      // No localStorage token or non-atomic lease is used as a lock.
      const locks = browser.navigator?.locks;
      inFlight = (locks ? locks.request(`stunm-refresh:${base.origin}${root}`, { signal: controller.signal }, renew) : renew())
        .finally(() => { clearTimeout(timeout); inFlight = null; refreshController = null; });
    }
    return inFlight;
  };
  const invalidate = () => {
    generation += 1;
    refreshController?.abort();
    renewalFailures = 0;
    renewalBlockedUntil = 0;
    renewalError = undefined;
  };
  browser.addEventListener('stunm:session-changing', invalidate);

  const requestId = client.interceptors.request.use((config) => {
    const url = resolve(config.url, config.baseURL);
    if (!isApi(url)) return config;
    if (excluded(url) && !config.__skipSessionRefresh) invalidate();
    config.withCredentials = config.withCredentials ?? true;
    const authorization = config.headers?.Authorization || config.headers?.authorization;
    if (typeof authorization === 'string' && /^Bearer\s*$/i.test(authorization.trim())) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return config;
  });
  const responseId = client.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    if (!config || config.__skipSessionRefresh || !shouldRefreshAxiosSession(error)) throw error;
    if (config.__sessionRetried) {
      // Renewal succeeded but the fresh session was still rejected. This is
      // now a genuine terminal authentication failure.
      error.sessionRefreshAttempted = true;
      error.sessionRefreshFailed = true;
      throw error;
    }
    const url = resolve(config.url, config.baseURL);
    if (!isApi(url) || excluded(url)) throw error;
    let data;
    try { data = await refresh(); } catch (refreshError) {
      // Only rejected refresh credentials require login. Network, server, origin
      // and cancellation errors should leave the current editor available to retry.
      error.sessionRefreshAttempted = true;
      error.sessionRefreshFailed = refreshError?.response?.status === 401;
      error.sessionRefreshError = refreshError;
      throw error;
    }
    if (config.signal?.aborted) throw error;
    config.__sessionRetried = true;
    config.headers = { ...config.headers, Authorization: `Bearer ${data.access_token}` };
    delete config.headers.authorization;
    return client(config);
  });
  browser.fetch = async (input, init = {}) => {
    const url = resolve(typeof input === 'string' || input instanceof URL ? String(input) : input.url);
    if (!isApi(url)) return nativeFetch(input, init);
    if (excluded(url)) invalidate();
    const options = { ...init, credentials: init.credentials || 'include' };
    const response = await nativeFetch(input, options);
    if (excluded(url) ||
        !(await shouldRefreshFetchSession(response))) return response;
    let data;
    try { data = await refresh(); } catch (refreshError) {
      // Fetch callers receive the original response; keep its body intact.
      try {
        response.sessionRefreshAttempted = true;
        response.sessionRefreshFailed = refreshError?.response?.status === 401;
      } catch (_) { /* Response may be read-only in some browser shims. */ }
      return response;
    }
    if ((init.signal || input?.signal)?.aborted) return response;
    const headers = new Headers(init.headers || input?.headers);
    headers.set('Authorization', `Bearer ${data.access_token}`);
    const retried = await nativeFetch(input, { ...options, headers });
    if (await shouldRefreshFetchSession(retried)) {
      retried.sessionRefreshAttempted = true;
      retried.sessionRefreshFailed = true;
    }
    return retried;
  };
  return () => {
    invalidate();
    client.interceptors.request.eject(requestId);
    client.interceptors.response.eject(responseId);
    browser.removeEventListener('stunm:session-changing', invalidate);
    browser.fetch = nativeFetch;
  };
}
